// Watches the staff session run down.
//
// The token lasts 24 hours and there is no refresh - /login issues one against
// a password, and nothing else does. So it can lapse with the app still open,
// and until now it did so silently: the inspector view kept working, a whole
// call could be taken, and the only sign was the record failing to save at the
// end. That is the worst possible moment to find out.
//
// This does not sign anybody out on its own. Clearing the session would unmount
// the call view, which mid-call means hanging up on a customer to enforce a
// login screen - a cure worse than the disease. It reports, and the UI decides.

import { useEffect, useState } from 'react';
import { msUntilExpiry } from './session';

/**
 * Long enough that a call started now would outlive the token. Inspections run
 * to half an hour, so a token with twenty minutes left is a call that fails to
 * save just as it finishes.
 */
export const EXPIRING_SOON_MS = 45 * 60 * 1000;

const TICK_MS = 30 * 1000;

export type SessionExpiry = {
  /** There is a token and it has not lapsed. */
  valid: boolean;
  /** Valid, but not for long enough to trust with a call. */
  expiringSoon: boolean;
  /** Milliseconds left; negative once lapsed, null when nothing is stored. */
  msLeft: number | null;
};

function read(): SessionExpiry {
  const msLeft = msUntilExpiry();
  const valid = msLeft !== null && msLeft > 0;
  return {
    valid,
    expiringSoon: valid && msLeft <= EXPIRING_SOON_MS,
    msLeft,
  };
}

export function useSessionExpiry(): SessionExpiry {
  const [expiry, setExpiry] = useState<SessionExpiry>(read);

  useEffect(() => {
    // Half a minute is often enough to catch the change while it still matters,
    // and cheap - it reads one localStorage key.
    const timer = setInterval(() => setExpiry(read()), TICK_MS);
    // A laptop that slept through the expiry fires no timer, so check on the
    // way back too.
    const onWake = () => setExpiry(read());
    window.addEventListener('focus', onWake);
    document.addEventListener('visibilitychange', onWake);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onWake);
      document.removeEventListener('visibilitychange', onWake);
    };
  }, []);

  return expiry;
}

/** "23 minutes", "4 hours" - for telling someone how long they have. */
export function formatRemaining(msLeft: number | null): string {
  if (msLeft === null || msLeft <= 0) return 'now';
  const minutes = Math.round(msLeft / 60000);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}
